import { Alert, AlertProps, Snackbar } from "@mui/material";
import React, { createContext, useContext, useState } from "react";

type TAlert = {
  severity: "success" | "error" | "info" | "warning";
  content: string;
  action?: React.ReactNode;
  props?: {
    sx?: object;
  };
};

/**
 * Create a ToastProvider that allows child components
 * to open a toast that is resoled via a promise.
 */
function CreateToastProvider() {
  const Context = createContext({
    alerts: [],
    open: false,
    show: () => undefined,
    showSuccess: () => undefined,
    showError: () => undefined,
    showInfo: () => undefined,
    showWarning: () => undefined,
  } as {
    alerts: TAlert[];
    open: boolean;
    show: (alert: string | TAlert) => void;
    showSuccess: (content: string, props?: Partial<AlertProps>) => void;
    showError: (content: string, props?: Partial<AlertProps>) => void;
    showInfo: (content: string, props?: Partial<AlertProps>) => void;
    showWarning: (content: string, props?: Partial<AlertProps>) => void;
  });

  const { Provider } = Context;

  // eslint-disable-next-line react/prop-types
  const ToastProvider = ({ children }: any) => {
    const [alerts, setAlerts] = useState<TAlert[]>([]);
    const [open, setOpen] = useState(false);

    const show = (alert: string | TAlert) => {
      if (!alert) {
        throw new Error("Invalid Alert");
      }

      // If the alert is a string, convert it into an object, otherwise, append it.
      if (typeof alert === "string") {
        // Add the alert to the list
        setAlerts([...alerts, { content: alert } as TAlert]);
      } else {
        setAlerts([...alerts, alert]);
      }

      setOpen(true);
    };

    /**
     * Show a success message
     *
     * @param content The message to display
     * @param props Any props for the alert
     * @returns void
     */
    const showSuccess = (content: string, props: Partial<AlertProps> = {}) =>
      show({ severity: "success", content, ...props });

    /**
     * Show a success message
     *
     * @param content The message to display
     * @param props Any props for the alert
     * @returns void
     */
    const showError = (content: string, props: Partial<AlertProps> = {}) =>
      show({ severity: "error", content, ...props });

    /**
     * Show a info message
     *
     * @param content The message to display
     * @param props Any props for the alert
     * @returns void
     */
    const showInfo = (content: string, props: Partial<AlertProps> = {}) =>
      show({ severity: "info", content, ...props });

    /**
     * Show a warning message
     *
     * @param content The message to display
     * @param props Any props for the alert
     * @returns void
     */
    const showWarning = (content: string, props: Partial<AlertProps> = {}) =>
      show({ severity: "warning", content, ...props });

    // Remove the first alert
    const handleClose = (event: any, reason: string) => {
      // Ignore Click Away behaviour
      // See: https://github.com/mui-org/material-ui/issues/5184#issuecomment-569418493
      if (reason === "clickaway") return;
      const updated = alerts.slice(1);
      setAlerts(updated);
      setOpen(false);

      if (updated.length) {
        setTimeout(() => setOpen(true), 300);
      }
    };

    // TODO: handle multiple Alerts
    return (
      <Provider
        value={{
          alerts,
          show,
          open,
          showSuccess,
          showError,
          showInfo,
          showWarning,
        }}
      >
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ maxWidth: { xs: 1, sm: 300 } }}
        >
          {alerts[0] && (
            <Alert
              severity={alerts[0]?.severity ?? "info"}
              icon={false}
              action={alerts[0]?.action ?? null}
              onClick={handleClose as any}
              sx={{
                flex: 1,
                "& .MuiAlert-message": {
                  width: 1,
                },
                ...(alerts[0]?.props?.sx || {}),
              }}
              {...(alerts[0].props || {})}
            >
              {alerts[0].content}
            </Alert>
          )}
        </Snackbar>
        {children}
      </Provider>
    );
  };

  const useAlerts = () => useContext(Context);

  return {
    ToastProvider,
    useAlerts,
  };
}

const { ToastProvider, useAlerts } = CreateToastProvider();

export { ToastProvider, useAlerts };
